import * as PIXI from 'pixi.js';
import MathUtils from '../../utils/MathUtils';
import type { FXParticle } from './FXParticle';
import type { FXParticleEmitterConfig } from './FXParticleEmitterConfig';
import { FXParticlePool } from './FXParticlePool';
import RandomDataGenerator from '../RandomDataGenerator';

const BASE_FPS = 60;
const FAST_FORWARD_FPS = 6;

export class FXParticleEmitter {
  private config: FXParticleEmitterConfig;

  private emitTime = 0;

  private nextSpawnAt = 0;

  private lastSpawnAt = 0;

  public spawnActive = true;

  private spawnUntil = 0;

  public x = 0;

  public y = 0;

  public emitX = 0;

  public emitY = 0;

  public emitAngle = 0;

  public container: PIXI.Container;

  private readonly activeParticles: FXParticle[];

  private readonly particlePool: FXParticlePool = FXParticlePool.getInstance();

  public readonly randomDataGenerator = new RandomDataGenerator([]);

  private readonly getRandomNumber = (): number => this.randomDataGenerator.rnd();

  constructor(config: FXParticleEmitterConfig, container?: PIXI.Container) {
    if (!config.keys && config.frames) {
      config.keys = config.frames;
    }

    this.config = config;

    this.container = container ?? new PIXI.Container();

    this.particlePool = FXParticlePool.getInstance();

    this.activeParticles = [];

    this.resetWithConfig(config);
  }

  public resetWithConfig(config: FXParticleEmitterConfig): void {
    this.config = config;
    this.emitTime = -config.startDelay;
    this.nextSpawnAt = 0;
    this.lastSpawnAt = 0;
    this.x = 0;
    this.y = 0;
    this.emitX = 0;
    this.emitY = 0;
    this.emitAngle = config.emitAngle;
    this.spawnUntil = Number.parseFloat(config.maxSpawnDuration.toString()) ?? 0;
    this.spawnActive = true;
    this.activeParticles.length = 0;

    // fast-forward time if set in config
    if (config.fastForward && config.fastForward > 0) this.fastForward(config.fastForward);
  }

  private getParticle(): FXParticle {
    return this.particlePool.getParticle();
  }

  private spawnParticle(emitAtAngle?: number): void {
    const { container, config } = this;
    const particle = this.getParticle();
    particle.init(config, this.getRandomNumber, this.emitAngle, emitAtAngle);
    particle.posX += this.emitX;
    particle.posY += this.emitY;
    if (config.spawnOnTopLayer) container.addChild(particle);
    else container.addChildAt(particle, 0);
    this.activeParticles.push(particle);
  }

  public setNextSpawnTime(): void {
    const { config } = this;
    const spawnDelay = Math.max(
      MathUtils.getRandomValueInRange(config.minSpawnDelay, config.maxSpawnDelay, this.getRandomNumber),
      1,
    );
    this.nextSpawnAt = this.lastSpawnAt + spawnDelay;
    this.lastSpawnAt = this.nextSpawnAt;
  }

  public resetSpawnTiming(): void {
    this.emitTime = -this.config.startDelay;
    this.nextSpawnAt = 0;
    this.lastSpawnAt = 0;
  }

  public update(deltaTime = 1): void {
    const dtSec = (1 / BASE_FPS) * deltaTime;
    const dtMs = dtSec * 1000;
    const newTime = this.emitTime + dtMs;

    if (this.emitTime < 0) {
      // handle negative time / startDelay
      if (newTime > 0) {
        // fix time to 0 when coming from negative
        this.emitTime = 0;
        this.nextSpawnAt = 0;
      } else {
        // if emitTime is negative don't want to do anything else
        this.emitTime = newTime;
        return;
      }
    }

    const maxSpawnDuration = this.getMaxSpawnDuration();

    // update particle spawning
    if (this.spawnActive) {
      if (newTime >= this.nextSpawnAt && this.nextSpawnAt <= maxSpawnDuration && this.emitTime <= maxSpawnDuration) {
        this.updateSpawning();
      } else if (this.emitTime === 0) {
        this.updateSpawning();
      }
    }

    this.emitTime = newTime;
    this.updateParticles(deltaTime, dtMs, dtSec);
  }

  private getMaxSpawnDuration(): number {
    const { maxSpawnDuration } = this.config;
    return typeof maxSpawnDuration === 'number' ? maxSpawnDuration : parseFloat(maxSpawnDuration);
  }

  private updateSpawning(): void {
    const { maxSimultaneousParticles } = this.config;
    if (maxSimultaneousParticles > 1) {
      // simultaneous distributed spawn behaviour
      this.spawnSimultaneousParticles();
    } else {
      // single particle spawn behaviour
      this.spawnSingleParticles();
    }
  }

  private updateParticles(deltaTime: number, dtMs: number, dtSec: number): void {
    // update configs
    const { friction, gravity, rotationFriction } = this.config;
    let particle;
    const dtGravity = gravity * dtSec;
    for (let i = this.activeParticles.length - 1; i >= 0; i -= 1) {
      particle = this.activeParticles[i];
      particle.updateParticle(dtMs, dtSec);

      // apply gravity and friction to active particles
      particle.posY += dtGravity;

      if (friction !== 0) {
        particle.velocity.x -= particle.velocity.x * friction * deltaTime;
        particle.velocity.y -= particle.velocity.y * friction * deltaTime;
      }

      particle.x = this.x + particle.posX;
      particle.y = this.y + particle.posY;

      if (rotationFriction !== 0) {
        particle.rotationVelocity -= particle.rotationVelocity * rotationFriction * deltaTime;
      }

      // remove expired particles
      if (particle.isExpired()) this.removeParticle(particle, i);
    }
  }

  private spawnSingleParticles(): void {
    const { maxParticles } = this.config;
    while (
      this.emitTime >= this.nextSpawnAt &&
      this.nextSpawnAt <= this.getMaxSpawnDuration() &&
      this.activeParticles.length < maxParticles
    ) {
      this.spawnParticle();
      this.setNextSpawnTime();
    }
  }

  private spawnSimultaneousParticles(): void {
    const { maxParticles, minSimultaneousParticles, maxSimultaneousParticles } = this.config;
    const simultaneousParticles = MathUtils.getRandomValueInRange(
      minSimultaneousParticles,
      maxSimultaneousParticles,
      this.getRandomNumber,
    );
    const { emitAngleVariance } = this.config;
    const emitAngle = this.emitAngle - (this.getRandomNumber() * emitAngleVariance) / 2;
    const angleIncrement = emitAngleVariance / simultaneousParticles;
    let angle = emitAngle - emitAngleVariance / 2;
    for (let i = 0; i < simultaneousParticles; i += 1) {
      if (this.activeParticles.length > maxParticles) break;
      angle += angleIncrement;
      this.spawnParticle(angle);
    }
    this.setNextSpawnTime();
  }

  /**
   * Stop emitting and clear timings. If you wish to restart use spawnActive = true.
   */
  public stop(): void {
    this.emitTime = 0;
    this.spawnUntil = 0;
    this.spawnActive = false;
  }

  /**
   * Fast-forward time
   */
  public fastForward(duration: number, fps = FAST_FORWARD_FPS): void {
    const fastForward = duration / 1000;
    const intervals = Math.ceil(fastForward * fps);
    for (let i = 0; i < intervals; i += 1) {
      if (this.isCompleted) return;
      this.update(BASE_FPS / fps);
    }
  }

  private removeParticle(particle: FXParticle, index: number): void {
    this.container.removeChild(particle);
    this.activeParticles.splice(index, 1);
    this.particlePool.addParticle(particle);
  }

  /**
   * Clears active configs and add them back to the pool
   */
  public clear(): void {
    while (this.activeParticles.length > 0) this.removeParticle(this.activeParticles[0], 0);
  }

  public destroy(): void {
    this.clear();
  }

  get isCompleted(): boolean {
    return this.emitTime >= this.spawnUntil && this.activeParticles.length === 0;
  }

  get activeParticleCount(): number {
    return this.activeParticles.length;
  }
}
